import React from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Settings,
  Trash2,
  Loader2,
  Clock,
  Database,
  FileJson,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { nodeTypeConfig } from '@/components/workflow/WorkflowNodeTypes';
import { nodeIconMap } from '@/components/workflow/NodePalette';
import { SchemaFieldMapper } from '@/components/workflow/SchemaFieldMapper';
import { SchemaDataStorePropertyPanel } from '@/components/workflow/SchemaDataStorePropertyPanel';
import { SchemaDataQueryPropertyPanel } from '@/components/workflow/SchemaDataQueryPropertyPanel';
import { BinaryParserPropertyPanel } from '@/components/workflow/BinaryParserPropertyPanel';
import { ADCRangeConverterPropertyPanel } from '@/components/workflow/ADCRangeConverterPropertyPanel';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScreenElement } from '@/stores/screens/screen-store';
import { useWorkflowEditor } from './WorkflowEditorContext.shared';
import { useKasaStore } from '@/integrations/kasa/kasa-store';
import { JsonValue, WorkflowVariableMapping } from '@/types/workflow-editor';

function getTextInputValue(value: unknown, fallback = ''): string | number {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  if (typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
}

function getSelectValue(value: unknown, fallback = ''): string | number | readonly string[] {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  if (
    Array.isArray(value) &&
    value.every((item): item is string => typeof item === 'string')
  ) {
    return value;
  }

  if (typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
}

function getTextareaValue(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value == null) {
    return fallback;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return fallback;
  }
}

function getDateTimeLocalValue(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function getDateDisplayValue(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString();
}

function getJsonValueForTextInput(value: JsonValue | undefined): string | number {
  return getTextInputValue(value);
}

/** Dynamic select with dropdown for discovered devices + manual IP entry fallback */
const DynamicDeviceSelect: React.FC<{
  value: string;
  onChange: (val: string) => void;
  required?: boolean;
}> = ({ value, onChange, required }) => {
  const { esp32Devices, fetchESP32Devices } = useKasaStore();
  const [loading, setLoading] = React.useState(false);
  const [manualMode, setManualMode] = React.useState(false);
  const fetched = React.useRef(false);

  React.useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
      if (esp32Devices.length === 0) {
        setLoading(true);
        fetchESP32Devices().finally(() => setLoading(false));
      }
    }
  }, [esp32Devices.length, fetchESP32Devices]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 text-sm text-muted-foreground border rounded-md">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading devices...
      </div>
    );
  }

  // Manual IP entry mode (or fallback when no devices discovered)
  if (manualMode || esp32Devices.length === 0) {
    return (
      <div className="space-y-2">
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter device IP (e.g. 10.10.88.94)"
        />
        {esp32Devices.length > 0 && (
          <button
            type="button"
            className="text-xs text-blue-600 hover:underline"
            onClick={() => setManualMode(false)}
          >
            Switch to device list
          </button>
        )}
        {esp32Devices.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No discovered devices found. Enter the device IP address directly.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <select
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {(!value || !required) && <option value="">Select a device...</option>}
        {esp32Devices.map((d) => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.alias || d.deviceId} ({d.host})
          </option>
        ))}
      </select>
      <button
        type="button"
        className="text-xs text-blue-600 hover:underline"
        onClick={() => setManualMode(true)}
      >
        Enter IP manually
      </button>
    </div>
  );
};

function getScreenElementLabel(element: ScreenElement): string {
  const label = element.properties.label;
  return typeof label === 'string' && label.trim() !== '' ? label : element.id;
}

function hasSchemaId<T extends { id?: string }>(schema: T): schema is T & { id: string } {
  return typeof schema.id === 'string' && schema.id.length > 0;
}

export const WorkflowPropertiesTab: React.FC = () => {
  const {
    selectedNodes,
    selectedNode,
    nodes,
    edges,
    setNodes,
    setSelectedNodes,
    deleteSelectedNodes,
    updateNodeData,
    updateNodeParameter,
    updateNodeConfig,
    workflowVariables,
    allAvailableVariables,
    validationResult,
    setValidationResult,
    isValidating,
    showVariablePicker,
    setShowVariablePicker,
    expressionInputRef,
    handleValidateExpression,
    handleInsertVariable,
    handleParseSampleMessage,
    getAvailableVariables,
    sampleParseError,
    setSampleParseError,
    onDemandSchema,
    schemas,
    devices,
    dataFilterOptions,
    screens,
    getNodeDefinition,
    workflowId,
  } = useWorkflowEditor();

  const schemasWithId = schemas.filter(hasSchemaId);
  const onDemandSchemaWithId = onDemandSchema && hasSchemaId(onDemandSchema) ? onDemandSchema : null;
  const esp32Devices = devices.filter((device) => device.type === 'esp32');
  const availableButtonElements = screens.flatMap((screen) =>
    screen.definition.elements
      .filter((element) => element.type === 'button')
      .map((button) => ({ screen, button })),
  );

  return (
    <>
          {/* Properties Tab Content - Multi-select view */}
          {selectedNodes.length > 1 ? (
            <div className="flex-1 p-4">
              <div className="text-center text-muted-foreground mb-4">
                <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium">{selectedNodes.length} nodes selected</p>
                <p className="text-xs mt-1">Select a single node to edit properties</p>
              </div>
              <div className="space-y-2 mt-4">
                <Label className="text-sm font-semibold">Selected Nodes:</Label>
                <ScrollArea className="h-48">
                  <div className="space-y-1">
                    {selectedNodes.map(node => {
                      const IconComponent = nodeIconMap[node.data.type as keyof typeof nodeIconMap];
                      return (
                        <div
                          key={node.id}
                          className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm"
                        >
                          {IconComponent && (
                            <IconComponent className="w-4 h-4 text-gray-500" />
                          )}
                          <span>{node.data.label || node.data.type}</span>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
              <Button
                variant="destructive"
                size="sm"
                className="w-full mt-4 gap-2"
                onClick={deleteSelectedNodes}
              >
                <Trash2 className="w-4 h-4" />
                Delete {selectedNodes.length} Nodes
              </Button>
            </div>
          ) : selectedNode ? (
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {/* Annotation Properties */}
                {selectedNode.type?.includes('annotation') ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">
                        {selectedNode.type === 'rectangle_annotation' ? 'Rectangle Group' : 'Text Label'}
                      </Label>
                    </div>

                    {/* Rectangle Annotation Properties */}
                    {selectedNode.type === 'rectangle_annotation' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="annotation-label">Group Label</Label>
                          <Input
                            id="annotation-label"
                            value={selectedNode.data.label || ''}
                            onChange={(e) => {
                              setNodes(nds => nds.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, label: e.target.value } }
                                  : n
                              ));
                            }}
                            placeholder="e.g., Page 1 Interactions"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="annotation-color">Color</Label>
                          <input
                            id="annotation-color"
                            type="color"
                            className="w-full h-10 rounded-md border cursor-pointer"
                            value={selectedNode.data.color || '#6B7280'}
                            onChange={(e) => {
                              setNodes(nds => nds.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, color: e.target.value } }
                                  : n
                              ));
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-2">
                            <Label htmlFor="annotation-width">Width</Label>
                            <Input
                              id="annotation-width"
                              type="number"
                              value={selectedNode.data.width || 200}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 100;
                                setNodes(nds => nds.map(n =>
                                  n.id === selectedNode.id
                                    ? { ...n, data: { ...n.data, width: value } }
                                    : n
                                ));
                              }}
                              min={100}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="annotation-height">Height</Label>
                            <Input
                              id="annotation-height"
                              type="number"
                              value={selectedNode.data.height || 150}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 80;
                                setNodes(nds => nds.map(n =>
                                  n.id === selectedNode.id
                                    ? { ...n, data: { ...n.data, height: value } }
                                    : n
                                ));
                              }}
                              min={80}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Label Annotation Properties */}
                    {selectedNode.type === 'label_annotation' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="annotation-text">Text</Label>
                          <Textarea
                            id="annotation-text"
                            value={selectedNode.data.text || ''}
                            onChange={(e) => {
                              setNodes(nds => nds.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, text: e.target.value } }
                                  : n
                              ));
                            }}
                            rows={3}
                            placeholder="Enter note text..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="annotation-fontsize">Font Size</Label>
                          <Input
                            id="annotation-fontsize"
                            type="number"
                            value={selectedNode.data.fontSize || 14}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 14;
                              setNodes(nds => nds.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, fontSize: value } }
                                  : n
                              ));
                            }}
                            min={10}
                            max={32}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="annotation-bgcolor">Background Color</Label>
                          <input
                            id="annotation-bgcolor"
                            type="color"
                            className="w-full h-10 rounded-md border cursor-pointer"
                            value={selectedNode.data.backgroundColor || '#FEF3C7'}
                            onChange={(e) => {
                              setNodes(nds => nds.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, backgroundColor: e.target.value } }
                                  : n
                              ));
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="annotation-textcolor">Text Color</Label>
                          <input
                            id="annotation-textcolor"
                            type="color"
                            className="w-full h-10 rounded-md border cursor-pointer"
                            value={selectedNode.data.textColor || '#92400E'}
                            onChange={(e) => {
                              setNodes(nds => nds.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, textColor: e.target.value } }
                                  : n
                              ));
                            }}
                          />
                        </div>
                      </>
                    )}

                    {/* Delete Annotation Button */}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
                        setSelectedNodes([]);
                      }}
                      className="w-full mt-4"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Annotation
                    </Button>
                  </>
                ) : (
                  /* Regular Node Properties */
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="node-label">Label</Label>
                      <Input
                        id="node-label"
                        value={selectedNode.data.label || ''}
                        onChange={(e) => updateNodeData('label', e.target.value)}
                        placeholder="Node label"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="node-description">Description</Label>
                      <Textarea
                    id="node-description"
                    value={selectedNode.data.description || ''}
                    onChange={(e) => updateNodeData('description', e.target.value)}
                    placeholder="Node description"
                    rows={3}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Node Type</Label>
                  <div className="text-sm text-muted-foreground">
                    {nodeTypeConfig[selectedNode.data.type as keyof typeof nodeTypeConfig]?.label ||
                      selectedNode.data.type}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Node ID</Label>
                  <div className="text-xs font-mono text-muted-foreground break-all">
                    {selectedNode.id}
                  </div>
                </div>

                <Separator />

                <Tabs defaultValue="config" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="config">Config</TabsTrigger>
                    <TabsTrigger value="data">Data</TabsTrigger>
                  </TabsList>
                  <TabsContent value="config" className="space-y-4 mt-4">
                    {/* Start Node Configuration */}
                    {selectedNode.data.type === 'start' && (
                      <>
                        <div className="space-y-4">
                          <Label className="text-base font-semibold">Trigger Configuration</Label>

                          {/* Trigger Type Selector */}
                          <div className="space-y-2">
                            <Label htmlFor="trigger-type">Trigger Type</Label>
                            <select
                              id="trigger-type"
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              value={selectedNode.data.config?.triggerType || 'manual'}
                              onChange={(e) => {
                                const newType = e.target.value;
                                updateNodeConfig('triggerType', newType);
                                // Auto-generate webhook secret when switching to webhook trigger
                                if (newType === 'webhook' && !selectedNode.data.config?.webhookSecret) {
                                  const array = new Uint8Array(32);
                                  crypto.getRandomValues(array);
                                  const secret = 'whsec_' + Array.from(array).map(b => b.toString(16).padStart(2, '0')).join(''); // pragma: allowlist secret
                                  updateNodeConfig('webhookSecret', secret);
                                }
                              }}
                            >
                              <option value="manual">Manual (Run Button)</option>
                              <option value="scheduled">Scheduled (Cron)</option>
                              <option value="mqtt">MQTT Topic</option>
                              <option value="device_data">Device Data (ESP32)</option>
                              <option value="webhook">Webhook (HTTP)</option>
                              <option value="unifi_protect_event">UniFi Protect Event</option>
                            </select>
                          </div>

                          {/* Cron Configuration - Only show if scheduled */}
                          {selectedNode.data.config?.triggerType === 'scheduled' && (
                            <>
                              <div className="space-y-4">
                                <Label className="text-sm font-semibold">Schedule Time</Label>

                                {/* Minute Selector */}
                                <div className="space-y-2">
                                  <Label htmlFor="cron-minute" className="text-xs">Minute</Label>
                                  <select
                                    id="cron-minute"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={(() => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const minute = cron.split(' ')[0];
                                      return minute === '*' ? '*' : minute.startsWith('*/') ? minute : 'specific';
                                    })()}
                                    onChange={(e) => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const parts = cron.split(' ');
                                      if (e.target.value === '*') {
                                        parts[0] = '*';
                                      } else if (e.target.value.startsWith('*/')) {
                                        parts[0] = e.target.value;
                                      } else {
                                        parts[0] = '0'; // Default to 0 for specific
                                      }
                                      updateNodeConfig('cron', parts.join(' '));
                                    }}
                                  >
                                    <option value="specific">Specific minute</option>
                                    <option value="*">Every minute</option>
                                    <option value="*/5">Every 5 minutes</option>
                                    <option value="*/10">Every 10 minutes</option>
                                    <option value="*/15">Every 15 minutes</option>
                                    <option value="*/30">Every 30 minutes</option>
                                  </select>
                                  {(() => {
                                    const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                    const minute = cron.split(' ')[0];
                                    return !minute.includes('*') && !minute.includes('/');
                                  })() && (
                                    <select
                                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                      value={(() => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        return cron.split(' ')[0] || '0';
                                      })()}
                                      onChange={(e) => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        const parts = cron.split(' ');
                                        parts[0] = e.target.value;
                                        updateNodeConfig('cron', parts.join(' '));
                                      }}
                                    >
                                      {Array.from({ length: 60 }, (_, i) => (
                                        <option key={i} value={i}>:{i.toString().padStart(2, '0')}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                                {/* Hour Selector */}
                                <div className="space-y-2">
                                  <Label htmlFor="cron-hour" className="text-xs">Hour</Label>
                                  <select
                                    id="cron-hour"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={(() => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const hour = cron.split(' ')[1];
                                      return hour === '*' ? '*' : hour.startsWith('*/') ? hour : 'specific';
                                    })()}
                                    onChange={(e) => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const parts = cron.split(' ');
                                      if (e.target.value === '*') {
                                        parts[1] = '*';
                                      } else if (e.target.value.startsWith('*/')) {
                                        parts[1] = e.target.value;
                                      } else {
                                        parts[1] = '9'; // Default to 9am
                                      }
                                      updateNodeConfig('cron', parts.join(' '));
                                    }}
                                  >
                                    <option value="specific">Specific hour</option>
                                    <option value="*">Every hour</option>
                                    <option value="*/2">Every 2 hours</option>
                                    <option value="*/3">Every 3 hours</option>
                                    <option value="*/6">Every 6 hours</option>
                                    <option value="*/12">Every 12 hours</option>
                                  </select>
                                  {(() => {
                                    const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                    const hour = cron.split(' ')[1];
                                    return !hour.includes('*') && !hour.includes('/');
                                  })() && (
                                    <select
                                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                      value={(() => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        return cron.split(' ')[1] || '9';
                                      })()}
                                      onChange={(e) => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        const parts = cron.split(' ');
                                        parts[1] = e.target.value;
                                        updateNodeConfig('cron', parts.join(' '));
                                      }}
                                    >
                                      {Array.from({ length: 24 }, (_, i) => (
                                        <option key={i} value={i}>
                                          {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                                {/* Day of Month Selector */}
                                <div className="space-y-2">
                                  <Label htmlFor="cron-day" className="text-xs">Day of Month</Label>
                                  <select
                                    id="cron-day"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={(() => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const day = cron.split(' ')[2];
                                      return day === '*' ? '*' : 'specific';
                                    })()}
                                    onChange={(e) => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const parts = cron.split(' ');
                                      parts[2] = e.target.value === '*' ? '*' : '1';
                                      updateNodeConfig('cron', parts.join(' '));
                                    }}
                                  >
                                    <option value="*">Every day</option>
                                    <option value="specific">Specific day</option>
                                  </select>
                                  {(() => {
                                    const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                    return cron.split(' ')[2] !== '*';
                                  })() && (
                                    <select
                                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                      value={(() => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        return cron.split(' ')[2] || '1';
                                      })()}
                                      onChange={(e) => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        const parts = cron.split(' ');
                                        parts[2] = e.target.value;
                                        updateNodeConfig('cron', parts.join(' '));
                                      }}
                                    >
                                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                        <option key={day} value={day}>Day {day}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                                {/* Month Selector */}
                                <div className="space-y-2">
                                  <Label htmlFor="cron-month" className="text-xs">Month</Label>
                                  <select
                                    id="cron-month"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={(() => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const month = cron.split(' ')[3];
                                      return month === '*' ? '*' : 'specific';
                                    })()}
                                    onChange={(e) => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const parts = cron.split(' ');
                                      parts[3] = e.target.value === '*' ? '*' : '1';
                                      updateNodeConfig('cron', parts.join(' '));
                                    }}
                                  >
                                    <option value="*">Every month</option>
                                    <option value="specific">Specific month</option>
                                  </select>
                                  {(() => {
                                    const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                    return cron.split(' ')[3] !== '*';
                                  })() && (
                                    <select
                                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                      value={(() => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        return cron.split(' ')[3] || '1';
                                      })()}
                                      onChange={(e) => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        const parts = cron.split(' ');
                                        parts[3] = e.target.value;
                                        updateNodeConfig('cron', parts.join(' '));
                                      }}
                                    >
                                      <option value="1">January</option>
                                      <option value="2">February</option>
                                      <option value="3">March</option>
                                      <option value="4">April</option>
                                      <option value="5">May</option>
                                      <option value="6">June</option>
                                      <option value="7">July</option>
                                      <option value="8">August</option>
                                      <option value="9">September</option>
                                      <option value="10">October</option>
                                      <option value="11">November</option>
                                      <option value="12">December</option>
                                    </select>
                                  )}
                                </div>

                                {/* Day of Week Selector */}
                                <div className="space-y-2">
                                  <Label htmlFor="cron-weekday" className="text-xs">Day of Week</Label>
                                  <select
                                    id="cron-weekday"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={(() => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const weekday = cron.split(' ')[4];
                                      return weekday === '*' ? '*' : 'specific';
                                    })()}
                                    onChange={(e) => {
                                      const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                      const parts = cron.split(' ');
                                      parts[4] = e.target.value === '*' ? '*' : '1';
                                      updateNodeConfig('cron', parts.join(' '));
                                    }}
                                  >
                                    <option value="*">Every day of week</option>
                                    <option value="specific">Specific day</option>
                                  </select>
                                  {(() => {
                                    const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                    return cron.split(' ')[4] !== '*';
                                  })() && (
                                    <select
                                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                      value={(() => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        return cron.split(' ')[4] || '1';
                                      })()}
                                      onChange={(e) => {
                                        const cron = selectedNode.data.config?.cron || '0 9 * * *';
                                        const parts = cron.split(' ');
                                        parts[4] = e.target.value;
                                        updateNodeConfig('cron', parts.join(' '));
                                      }}
                                    >
                                      <option value="0">Sunday</option>
                                      <option value="1">Monday</option>
                                      <option value="2">Tuesday</option>
                                      <option value="3">Wednesday</option>
                                      <option value="4">Thursday</option>
                                      <option value="5">Friday</option>
                                      <option value="6">Saturday</option>
                                    </select>
                                  )}
                                </div>
                              </div>

                              {/* Human-readable description */}
                              <div className="text-sm bg-blue-50 p-3 rounded border border-blue-200">
                                <strong>Schedule:</strong> {selectedNode.data.config?.cron || '0 9 * * *'}
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="timezone">Timezone</Label>
                                <select
                                  id="timezone"
                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  value={selectedNode.data.config?.timezone || 'America/Los_Angeles'}
                                  onChange={(e) => updateNodeConfig('timezone', e.target.value)}
                                >
                                  <option value="America/Los_Angeles">Pacific Time</option>
                                  <option value="America/Denver">Mountain Time</option>
                                  <option value="America/Chicago">Central Time</option>
                                  <option value="America/New_York">Eastern Time</option>
                                  <option value="UTC">UTC</option>
                                </select>
                              </div>

                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="cron-enabled"
                                  checked={selectedNode.data.config?.enabled !== false}
                                  onChange={(e) => updateNodeConfig('enabled', e.target.checked)}
                                  className="h-4 w-4"
                                />
                                <Label htmlFor="cron-enabled">Enabled</Label>
                              </div>
                            </>
                          )}

                          {/* MQTT Configuration - Only show if mqtt */}
                          {selectedNode.data.config?.triggerType === 'mqtt' && (
                            <>
                              <div className="space-y-4">
                                <Label className="text-sm font-semibold">MQTT Topic Configuration</Label>

                                {/* MQTT Topic Input */}
                                <div className="space-y-2">
                                  <Label htmlFor="mqtt-topic" className="text-xs">Topic</Label>
                                  <input
                                    id="mqtt-topic"
                                    type="text"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                                    placeholder="e.g., sensor/water/# or lora/device/+/data"
                                    value={selectedNode.data.config?.mqttTopic || ''}
                                    onChange={(e) => updateNodeConfig('mqttTopic', e.target.value)}
                                    title="MQTT topic pattern - supports + (single level) and # (multi-level) wildcards"
                                  />
                                  <div className="text-xs bg-gray-50 p-2 rounded border">
                                    <p className="font-medium mb-1">Wildcard patterns:</p>
                                    <ul className="space-y-1 ml-3">
                                      <li>• <code className="bg-white px-1 rounded">+</code> = single level (e.g., <code className="bg-white px-1 rounded">sensor/+/data</code>)</li>
                                      <li>• <code className="bg-white px-1 rounded">#</code> = multi-level (e.g., <code className="bg-white px-1 rounded">sensor/#</code>)</li>
                                    </ul>
                                  </div>
                                </div>

                                {/* Topic preview */}
                                {selectedNode.data.config?.mqttTopic && (
                                  <div className="text-sm bg-blue-50 p-3 rounded border border-blue-200">
                                    <strong>Subscribing to:</strong> {selectedNode.data.config?.mqttTopic}
                                  </div>
                                )}

                                {/* Sample MQTT Message - Auto-generate mappings */}
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <Label className="text-sm font-semibold">Sample MQTT Message</Label>
                                  </div>
                                  <div className="text-xs bg-green-50 p-3 rounded border border-green-200">
                                    <p className="text-green-900">
                                      <strong>Paste a sample JSON message to auto-generate variable mappings</strong>
                                    </p>
                                  </div>
                                  <Textarea
                                    placeholder={`Paste sample JSON, e.g.:
{
  "temp": 72.5,
  "humidity": 45,
  "device": {
    "id": "sensor_001",
    "status": "online"
  }
}`}
                                    value={selectedNode.data.config?.sampleMessage || ''}
                                    onChange={(e) => {
                                      updateNodeConfig('sampleMessage', e.target.value);
                                      setSampleParseError(null);
                                    }}
                                    rows={6}
                                    className="font-mono text-xs"
                                  />
                                  {sampleParseError && (
                                    <p className="text-sm text-red-500">{sampleParseError}</p>
                                  )}
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleParseSampleMessage}
                                    className="w-full"
                                  >
                                    <FileJson className="h-4 w-4 mr-2" />
                                    Parse & Generate Mappings
                                  </Button>
                                </div>

                                {/* Variable Mappings */}
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <Label className="text-sm font-semibold">Variable Mappings</Label>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentMappings = selectedNode.data.config?.variableMappings || [];
                                        updateNodeConfig('variableMappings', [
                                          ...currentMappings,
                                          { sourcePath: '', targetVariable: '' }
                                        ]);
                                      }}
                                      className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                    >
                                      + Add Mapping
                                    </button>
                                  </div>
                                  <div className="text-xs bg-blue-50 p-3 rounded border border-blue-200 space-y-2">
                                    <p className="text-blue-900">
                                      <strong>📋 How it works:</strong>
                                    </p>
                                    <ol className="list-decimal list-inside space-y-1 text-blue-800 ml-2">
                                      <li>MQTT message arrives with JSON payload</li>
                                      <li>System wraps it as <code className="bg-blue-100 px-1 rounded">triggerConfig.message</code></li>
                                      <li>Your mappings extract values to named variables</li>
                                      <li>Variables become available in all workflow nodes</li>
                                    </ol>
                                    <p className="text-blue-700 pt-2 border-t border-blue-200">
                                      💡 Use dot notation for nested properties: <code className="bg-white px-1 rounded">data.sensor.value</code>
                                    </p>
                                  </div>

                                  {/* Mapping List */}
                                  {selectedNode.data.config?.variableMappings?.map((mapping: WorkflowVariableMapping, index: number) => (
                                    <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded border">
                                      <div className="flex-1 space-y-2">
                                        <div>
                                          <Label htmlFor={`mapping-source-${index}`} className="text-xs">Source Path</Label>
                                          <input
                                            id={`mapping-source-${index}`}
                                            type="text"
                                            placeholder="e.g., triggerConfig.message.data.water_height_feet"
                                            value={mapping.sourcePath || ''}
                                            onChange={(e) => {
                                              const currentMappings = [...(selectedNode.data.config?.variableMappings || [])];
                                              currentMappings[index] = { ...currentMappings[index], sourcePath: e.target.value };
                                              updateNodeConfig('variableMappings', currentMappings);
                                            }}
                                            className="flex h-9 w-full rounded-md border border-input bg-white px-2 py-1 text-sm font-mono"
                                            title="Use dot notation to access nested JSON properties from MQTT message payload"
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor={`mapping-target-${index}`} className="text-xs">Variable Name</Label>
                                          <input
                                            id={`mapping-target-${index}`}
                                            type="text"
                                            placeholder="e.g., waterHeight"
                                            value={mapping.targetVariable || ''}
                                            onChange={(e) => {
                                              const currentMappings = [...(selectedNode.data.config?.variableMappings || [])];
                                              currentMappings[index] = { ...currentMappings[index], targetVariable: e.target.value };
                                              updateNodeConfig('variableMappings', currentMappings);
                                            }}
                                            className="flex h-9 w-full rounded-md border border-input bg-white px-2 py-1 text-sm font-mono"
                                            title="Variable name to create (accessible in Set Variable and other nodes)"
                                          />
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const currentMappings = [...(selectedNode.data.config?.variableMappings || [])];
                                          currentMappings.splice(index, 1);
                                          updateNodeConfig('variableMappings', currentMappings);
                                        }}
                                        className="mt-6 text-red-500 hover:text-red-700"
                                        title="Remove mapping"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}

                                  {/* Examples */}
                                  {(!selectedNode.data.config?.variableMappings || selectedNode.data.config.variableMappings.length === 0) && (
                                    <div className="text-xs bg-blue-50 p-3 rounded border border-blue-200">
                                      <strong className="text-blue-900">Agricultural Sensor Examples:</strong>
                                      <div className="mt-2 space-y-2">
                                        <div className="bg-white p-2 rounded border border-blue-100">
                                          <div className="font-medium text-blue-800 mb-1">Water Level Sensor:</div>
                                          <ul className="space-y-1 ml-3">
                                            <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">triggerConfig.message.data.water_height_feet</code> → waterHeight</li>
                                            <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">triggerConfig.message.data.battery_volt</code> → batteryVoltage</li>
                                            <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">triggerConfig.message.device.id</code> → deviceId</li>
                                          </ul>
                                        </div>
                                        <div className="bg-white p-2 rounded border border-blue-100">
                                          <div className="font-medium text-blue-800 mb-1">Soil Moisture Sensor:</div>
                                          <ul className="space-y-1 ml-3">
                                            <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">triggerConfig.message.data.soil_moisture_percent</code> → soilMoisture</li>
                                            <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">triggerConfig.message.data.temperature_celsius</code> → temperature</li>
                                          </ul>
                                        </div>
                                        <div className="bg-white p-2 rounded border border-blue-100">
                                          <div className="font-medium text-blue-800 mb-1">Location & Metadata:</div>
                                          <ul className="space-y-1 ml-3">
                                            <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">triggerConfig.message.location.latitude</code> → latitude</li>
                                            <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">triggerConfig.message.location.longitude</code> → longitude</li>
                                            <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">triggerConfig.topic</code> → mqttTopic</li>
                                          </ul>
                                        </div>
                                      </div>
                                      <div className="mt-2 pt-2 border-t border-blue-200 text-blue-700">
                                        <strong>💡 Tip:</strong> Your MQTT message JSON structure determines available paths. Use dot notation for nested properties.
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id="mqtt-enabled"
                                    checked={selectedNode.data.config?.enabled !== false}
                                    onChange={(e) => updateNodeConfig('enabled', e.target.checked)}
                                    className="h-4 w-4"
                                  />
                                  <Label htmlFor="mqtt-enabled">Enabled</Label>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Device Data Trigger Configuration - Only show if device_data */}
                          {selectedNode.data.config?.triggerType === 'device_data' && (
                            <>
                              <div className="space-y-4">
                                <Label className="text-sm font-semibold">Device Data Filter Configuration</Label>
                                <div className="text-xs bg-green-50 p-3 rounded border border-green-200">
                                  <p className="text-green-900">
                                    <strong>📡 ESP32 Device Data Trigger</strong>
                                  </p>
                                  <p className="mt-1 text-green-800">
                                    This workflow will trigger when an ESP32 device sends data via the <code className="bg-green-100 px-1 rounded">send_data</code> node.
                                    All filters are optional - leave empty to match all data.
                                  </p>
                                </div>

                                {/* Data Type Filter - Dropdown */}
                                <div className="space-y-2">
                                  <Label htmlFor="device-data-type" className="text-xs">Data Type</Label>
                                  <select
                                    id="device-data-type"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={selectedNode.data.config?.deviceDataFilters?.dataType || ''}
                                    onChange={(e) => {
                                      const currentFilters = selectedNode.data.config?.deviceDataFilters || {};
                                      updateNodeConfig('deviceDataFilters', { ...currentFilters, dataType: e.target.value });
                                    }}
                                    title="Filter by data type from send_data node"
                                  >
                                    <option value="">Any data type</option>
                                    {dataFilterOptions.dataTypes.map((dt: string) => (
                                      <option key={dt} value={dt}>{dt}</option>
                                    ))}
                                  </select>
                                  {dataFilterOptions.dataTypes.length === 0 && (
                                    <p className="text-xs text-muted-foreground">No data types found yet. Data types appear after devices send data.</p>
                                  )}
                                </div>

                                {/* Device ID Filter - Dropdown */}
                                <div className="space-y-2">
                                  <Label htmlFor="device-data-device-id" className="text-xs">Device</Label>
                                  <select
                                    id="device-data-device-id"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={selectedNode.data.config?.deviceDataFilters?.deviceId || ''}
                                    onChange={(e) => {
                                      const currentFilters = selectedNode.data.config?.deviceDataFilters || {};
                                      updateNodeConfig('deviceDataFilters', { ...currentFilters, deviceId: e.target.value });
                                    }}
                                    title="Filter by specific device (* matches any)"
                                  >
                                    <option value="">Any device</option>
                                    <option value="*">* (All devices)</option>
                                    {esp32Devices
                                      .map((device) => (
                                        <option key={device.id} value={device.deviceId}>
                                          {device.name || device.deviceId} {device.status === 'online' ? '🟢' : '⚫'}
                                        </option>
                                      ))
                                    }
                                  </select>
                                  {esp32Devices.length === 0 && (
                                    <p className="text-xs text-amber-600">No ESP32 devices found. Register devices first.</p>
                                  )}
                                </div>

                                {/* Schema ID Filter - Dropdown */}
                                <div className="space-y-2">
                                  <Label htmlFor="device-data-schema-id" className="text-xs">Schema (Optional)</Label>
                                  <select
                                    id="device-data-schema-id"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={selectedNode.data.config?.deviceDataFilters?.schemaId || ''}
                                    onChange={(e) => {
                                      const currentFilters = selectedNode.data.config?.deviceDataFilters || {};
                                      updateNodeConfig('deviceDataFilters', { ...currentFilters, schemaId: e.target.value });
                                    }}
                                    title="Filter by schema used for validation"
                                  >
                                    <option value="">Any schema</option>
                                    {schemas.map((schema) => (
                                      <option key={schema.id} value={schema.id}>
                                        {schema.name} ({schema.fields?.length || 0} fields)
                                      </option>
                                    ))}
                                  </select>
                                  {schemas.length === 0 && (
                                    <p className="text-xs text-amber-600">No schemas found. Create schemas in the Schemas tab.</p>
                                  )}
                                </div>

                                {/* ESP32 Workflow Filter - Dropdown */}
                                <div className="space-y-2">
                                  <Label htmlFor="device-data-workflow-id" className="text-xs">ESP32 Workflow (Optional)</Label>
                                  <select
                                    id="device-data-workflow-id"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={selectedNode.data.config?.deviceDataFilters?.workflowId || ''}
                                    onChange={(e) => {
                                      const currentFilters = selectedNode.data.config?.deviceDataFilters || {};
                                      updateNodeConfig('deviceDataFilters', { ...currentFilters, workflowId: e.target.value });
                                    }}
                                    title="Filter by ESP32 workflow that sent the data"
                                  >
                                    <option value="">Any workflow</option>
                                    {dataFilterOptions.esp32Workflows.map((wf) => (
                                      <option key={wf.id} value={wf.id}>
                                        {wf.name} ({wf.status})
                                      </option>
                                    ))}
                                    {/* Also show workflow IDs from device data that may not match current workflows */}
                                    {dataFilterOptions.esp32WorkflowIds
                                      .filter((id: string) => !dataFilterOptions.esp32Workflows.some((wf) => wf.id === id))
                                      .map((id: string) => (
                                        <option key={id} value={id}>
                                          {id} (historical)
                                        </option>
                                      ))
                                    }
                                  </select>
                                  {dataFilterOptions.esp32Workflows.length === 0 && dataFilterOptions.esp32WorkflowIds.length === 0 && (
                                    <p className="text-xs text-muted-foreground">No ESP32 workflows found. Create an ESP32 workflow first.</p>
                                  )}
                                </div>

                                {/* Custom ID Filter - Dropdown */}
                                <div className="space-y-2">
                                  <Label htmlFor="device-data-custom-id" className="text-xs">Custom ID (Optional)</Label>
                                  <select
                                    id="device-data-custom-id"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={selectedNode.data.config?.deviceDataFilters?.customId || ''}
                                    onChange={(e) => {
                                      const currentFilters = selectedNode.data.config?.deviceDataFilters || {};
                                      updateNodeConfig('deviceDataFilters', { ...currentFilters, customId: e.target.value });
                                    }}
                                    title="Filter by user-defined custom identifier"
                                  >
                                    <option value="">Any custom ID</option>
                                    {dataFilterOptions.customIds.map((id: string) => (
                                      <option key={id} value={id}>{id}</option>
                                    ))}
                                  </select>
                                  {dataFilterOptions.customIds.length === 0 && (
                                    <p className="text-xs text-muted-foreground">No custom IDs found yet. Set customId in the ESP32 send_data node.</p>
                                  )}
                                </div>

                                {/* Filter Preview */}
                                {(() => {
                                  const deviceDataFilters =
                                    selectedNode.data.config?.deviceDataFilters;

                                  if (
                                    !deviceDataFilters?.dataType &&
                                    !deviceDataFilters?.deviceId &&
                                    !deviceDataFilters?.schemaId &&
                                    !deviceDataFilters?.workflowId &&
                                    !deviceDataFilters?.customId
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <div className="text-sm bg-green-50 p-3 rounded border border-green-200">
                                      <strong>Active Filters:</strong>
                                      <ul className="mt-1 space-y-1">
                                        {deviceDataFilters.dataType && (
                                          <li>• Data Type: <code className="bg-white px-1 rounded">{deviceDataFilters.dataType}</code></li>
                                        )}
                                        {deviceDataFilters.deviceId && (
                                          <li>• Device: <code className="bg-white px-1 rounded">
                                            {deviceDataFilters.deviceId === '*'
                                              ? '* (All devices)'
                                              : devices.find((device) => device.deviceId === deviceDataFilters.deviceId)?.name
                                                || deviceDataFilters.deviceId}
                                          </code></li>
                                        )}
                                        {deviceDataFilters.schemaId && (
                                          <li>• Schema: <code className="bg-white px-1 rounded">
                                            {schemas.find((schema) => schema.id === deviceDataFilters.schemaId)?.name
                                              || deviceDataFilters.schemaId}
                                          </code></li>
                                        )}
                                        {deviceDataFilters.workflowId && (
                                          <li>• ESP32 Workflow: <code className="bg-white px-1 rounded">
                                            {dataFilterOptions.esp32Workflows.find((workflow) => workflow.id === deviceDataFilters.workflowId)?.name
                                              || deviceDataFilters.workflowId}
                                          </code></li>
                                        )}
                                        {deviceDataFilters.customId && (
                                          <li>• Custom ID: <code className="bg-white px-1 rounded">{deviceDataFilters.customId}</code></li>
                                        )}
                                      </ul>
                                    </div>
                                  );
                                })()}

                                {/* Available Variables */}
                                <div className="text-xs bg-blue-50 p-3 rounded border border-blue-200">
                                  <p className="text-blue-900">
                                    <strong>📋 Available Variables (dot notation):</strong>
                                  </p>
                                  <div className="mt-2 space-y-2">
                                    <div className="bg-white p-2 rounded border border-blue-100">
                                      <div className="font-medium text-blue-800 mb-1">Device Data (payload):</div>
                                      <ul className="space-y-1 ml-3">
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceData.temperature</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceData.humidity</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceData.&lt;any_field&gt;</code></li>
                                      </ul>
                                    </div>
                                    <div className="bg-white p-2 rounded border border-blue-100">
                                      <div className="font-medium text-blue-800 mb-1">Device Metadata:</div>
                                      <ul className="space-y-1 ml-3">
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceMeta.deviceId</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceMeta.dataType</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceMeta.timestamp</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceMeta.workflowId</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceMeta.schemaId</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceMeta.customId</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceMeta.rssi</code></li>
                                        <li>• <code className="bg-blue-100 px-1 py-0.5 rounded">deviceMeta.firmwareVersion</code></li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>

                                {/* Variable Mappings */}
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <Label className="text-sm font-semibold">Custom Variable Mappings (Optional)</Label>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentMappings = selectedNode.data.config?.deviceDataVariableMappings || [];
                                        updateNodeConfig('deviceDataVariableMappings', [
                                          ...currentMappings,
                                          { sourcePath: '', targetVariable: '' }
                                        ]);
                                      }}
                                      className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                    >
                                      + Add Mapping
                                    </button>
                                  </div>

                                  {/* Mapping List */}
                                  {selectedNode.data.config?.deviceDataVariableMappings?.map((mapping: WorkflowVariableMapping, index: number) => (
                                    <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded border">
                                      <div className="flex-1 space-y-2">
                                        <div>
                                          <Label htmlFor={`dd-mapping-source-${index}`} className="text-xs">Source Path</Label>
                                          <input
                                            id={`dd-mapping-source-${index}`}
                                            type="text"
                                            placeholder="e.g., deviceData.temperature"
                                            value={mapping.sourcePath || ''}
                                            onChange={(e) => {
                                              const currentMappings = [...(selectedNode.data.config?.deviceDataVariableMappings || [])];
                                              currentMappings[index] = { ...currentMappings[index], sourcePath: e.target.value };
                                              updateNodeConfig('deviceDataVariableMappings', currentMappings);
                                            }}
                                            className="flex h-9 w-full rounded-md border border-input bg-white px-2 py-1 text-sm font-mono"
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor={`dd-mapping-target-${index}`} className="text-xs">Variable Name</Label>
                                          <input
                                            id={`dd-mapping-target-${index}`}
                                            type="text"
                                            placeholder="e.g., temp"
                                            value={mapping.targetVariable || ''}
                                            onChange={(e) => {
                                              const currentMappings = [...(selectedNode.data.config?.deviceDataVariableMappings || [])];
                                              currentMappings[index] = { ...currentMappings[index], targetVariable: e.target.value };
                                              updateNodeConfig('deviceDataVariableMappings', currentMappings);
                                            }}
                                            className="flex h-9 w-full rounded-md border border-input bg-white px-2 py-1 text-sm font-mono"
                                          />
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const currentMappings = [...(selectedNode.data.config?.deviceDataVariableMappings || [])];
                                          currentMappings.splice(index, 1);
                                          updateNodeConfig('deviceDataVariableMappings', currentMappings);
                                        }}
                                        className="mt-6 text-red-500 hover:text-red-700"
                                        title="Remove mapping"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>

                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id="device-data-enabled"
                                    checked={selectedNode.data.config?.deviceDataEnabled !== false}
                                    onChange={(e) => updateNodeConfig('deviceDataEnabled', e.target.checked)}
                                    className="h-4 w-4"
                                  />
                                  <Label htmlFor="device-data-enabled">Enabled</Label>
                                </div>
                              </div>
                            </>
                          )}

                          {/* UniFi Protect Event Trigger Configuration */}
                          {selectedNode.data.config?.triggerType === 'unifi_protect_event' && (
                            <>
                              <div className="space-y-4">
                                <Label className="text-sm font-semibold">UniFi Protect Event Trigger</Label>

                                {/* Camera filter */}
                                <div className="space-y-2">
                                  <Label htmlFor="unifi-camera-id" className="text-xs">Camera ID (optional — leave blank for any camera)</Label>
                                  <Input
                                    id="unifi-camera-id"
                                    placeholder="e.g. abc123def456 (leave blank for any)"
                                    value={selectedNode.data.config?.unifiCameraId || ''}
                                    onChange={(e) => updateNodeConfig('unifiCameraId', e.target.value)}
                                  />
                                </div>

                                {/* Event type filter */}
                                <div className="space-y-2">
                                  <Label className="text-xs">Event Types (leave all unchecked for any)</Label>
                                  <div className="grid grid-cols-2 gap-2">
                                    {[
                                      { value: 'motion',        label: 'Motion' },
                                      { value: 'person',        label: 'Person' },
                                      { value: 'vehicle',       label: 'Vehicle' },
                                      { value: 'animal',        label: 'Animal' },
                                      { value: 'licensePlate',  label: 'License Plate' },
                                      { value: 'ring',          label: 'Doorbell Ring' },
                                      { value: 'audioDetect',   label: 'Audio Detect' },
                                    ].map(({ value, label }) => {
                                      const current: string[] = selectedNode.data.config?.unifiEventTypes ?? [];
                                      const checked = current.includes(value);
                                      return (
                                        <label key={value} className="flex items-center gap-2 text-xs cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                              const next = e.target.checked
                                                ? [...current, value]
                                                : current.filter((v: string) => v !== value);
                                              updateNodeConfig('unifiEventTypes', next);
                                            }}
                                          />
                                          {label}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Available variables */}
                                <div className="text-xs bg-blue-50 p-3 rounded border border-blue-200">
                                  <strong className="text-blue-900">Available variables:</strong>
                                  <ul className="mt-1 space-y-0.5 ml-3 text-blue-800">
                                    <li><code className="bg-blue-100 px-1 rounded">event.type</code> — motion / smartDetectZone / ring / audioDetect</li>
                                    <li><code className="bg-blue-100 px-1 rounded">event.cameraId</code></li>
                                    <li><code className="bg-blue-100 px-1 rounded">event.cameraName</code></li>
                                    <li><code className="bg-blue-100 px-1 rounded">event.score</code> — confidence 0–100</li>
                                    <li><code className="bg-blue-100 px-1 rounded">event.smartDetectTypes</code> — array</li>
                                    <li><code className="bg-blue-100 px-1 rounded">event.start</code> — Unix ms timestamp</li>
                                  </ul>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Webhook Configuration - Only show if webhook */}
                          {selectedNode.data.config?.triggerType === 'webhook' && (
                            <>
                              <div className="space-y-4">
                                <Label className="text-sm font-semibold">Webhook Configuration</Label>

                                <div className="text-xs bg-purple-50 p-3 rounded border border-purple-200">
                                  <p className="text-purple-900">
                                    <strong>🔗 HTTP Webhook Trigger</strong>
                                  </p>
                                  <p className="mt-1 text-purple-800">
                                    This workflow will trigger when an HTTP POST request is sent to the webhook URL with the correct secret.
                                  </p>
                                </div>

                                {/* Webhook URL (readonly, computed from workflow ID) */}
                                <div className="space-y-2">
                                  <Label className="text-xs">Webhook URL</Label>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      className="flex h-10 w-full rounded-md border border-input bg-gray-100 px-3 py-2 text-sm font-mono"
                                      value={`${window.location.origin.replace(':3001', ':8001')}/webhooks/${workflowId}`}
                                      readOnly
                                    />
                                    <button
                                      type="button"
                                      className="px-3 py-2 text-sm border rounded-md hover:bg-gray-100"
                                      onClick={() => navigator.clipboard.writeText(`${window.location.origin.replace(':3001', ':8001')}/webhooks/${workflowId}`)}
                                      title="Copy URL"
                                    >
                                      📋
                                    </button>
                                  </div>
                                </div>

                                {/* Webhook Secret */}
                                <div className="space-y-2">
                                  <Label className="text-xs">Webhook Secret</Label>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      className="flex h-10 w-full rounded-md border border-input bg-gray-100 px-3 py-2 text-sm font-mono"
                                      value={selectedNode.data.config?.webhookSecret || '(auto-generated on publish)'}
                                      readOnly
                                    />
                                    {selectedNode.data.config?.webhookSecret && (
                                      <button
                                        type="button"
                                        className="px-3 py-2 text-sm border rounded-md hover:bg-gray-100"
                                        onClick={() => navigator.clipboard.writeText(selectedNode.data.config?.webhookSecret || '')}
                                        title="Copy Secret"
                                      >
                                        📋
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    Include this in the <code className="bg-gray-100 px-1 rounded">X-Webhook-Secret</code> header when calling the webhook.
                                  </p>
                                </div>

                                {/* Usage Instructions */}
                                <div className="text-xs bg-blue-50 p-3 rounded border border-blue-200">
                                  <p className="font-medium mb-2">Usage Example:</p>
                                  <pre className="bg-white p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
{`curl -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-Webhook-Secret: <secret>" \\
  -d '{"key": "value"}' \\
  <webhook-url>`}
                                  </pre>
                                </div>

                                {/* Available Variables */}
                                <div className="text-xs bg-green-50 p-3 rounded border border-green-200">
                                  <p className="text-green-900">
                                    <strong>📋 Available Variables:</strong>
                                  </p>
                                  <ul className="mt-2 space-y-1 ml-3">
                                    <li>• <code className="bg-white px-1 rounded">webhookPayload</code> - The JSON body of the request</li>
                                    <li>• <code className="bg-white px-1 rounded">webhookHeaders</code> - Request headers</li>
                                    <li>• <code className="bg-white px-1 rounded">webhookSourceIP</code> - Client IP address</li>
                                    <li>• <code className="bg-white px-1 rounded">webhookTimestamp</code> - Request timestamp</li>
                                  </ul>
                                </div>

                                {/* Enable/Disable */}
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id="webhook-enabled"
                                    checked={selectedNode.data.config?.enabled !== false}
                                    onChange={(e) => updateNodeConfig('enabled', e.target.checked)}
                                    className="h-4 w-4"
                                  />
                                  <Label htmlFor="webhook-enabled">Enabled</Label>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Loop Pairing Configuration */}
                          <div className="space-y-4 pt-4 border-t">
                            <Label className="text-base font-semibold">Loop Configuration</Label>
                            <p className="text-xs text-muted-foreground">
                              Pair this Start with an End node to create an interaction loop (e.g., for page navigation)
                            </p>

                            {/* Loop Name */}
                            <div className="space-y-2">
                              <Label htmlFor="loop-name">Loop Name</Label>
                              <input
                                id="loop-name"
                                type="text"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                placeholder="e.g., Home Screen, Settings Page"
                                value={selectedNode.data.config?.name || ''}
                                onChange={(e) => updateNodeConfig('name', e.target.value)}
                              />
                            </div>

                            {/* Paired End Node Selector */}
                            <div className="space-y-2">
                              <Label htmlFor="paired-end">Paired End Node</Label>
                              <select
                                id="paired-end"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={selectedNode.data.config?.pairedEndId || ''}
                                onChange={(e) => {
                                  const endNodeId = e.target.value;
                                  const startName = selectedNode.data.config?.name || 'Unnamed Loop';
                                  const previousEndId = selectedNode.data.config?.pairedEndId;

                                  // Update this Start node's pairedEndId
                                  updateNodeConfig('pairedEndId', endNodeId || undefined);

                                  // Clear pairing from previously paired End node
                                  if (previousEndId && previousEndId !== endNodeId) {
                                    setNodes(nds => nds.map(n =>
                                      n.id === previousEndId
                                        ? { ...n, data: { ...n.data, config: { ...n.data.config, pairedStartId: undefined, pairedStartName: undefined } } }
                                        : n
                                    ));
                                  }

                                  // Set pairing on newly paired End node
                                  if (endNodeId) {
                                    setNodes(nds => nds.map(n =>
                                      n.id === endNodeId
                                        ? { ...n, data: { ...n.data, config: { ...n.data.config, pairedStartId: selectedNode.id, pairedStartName: startName } } }
                                        : n
                                    ));
                                  }
                                }}
                              >
                                <option value="">None (workflow terminates at End)</option>
                                {nodes
                                  .filter(n => n.data?.type === 'end')
                                  .filter(n => !n.data.config?.pairedStartId || n.data.config?.pairedStartId === selectedNode.id)
                                  .map(endNode => (
                                    <option key={endNode.id} value={endNode.id}>
                                      {endNode.data.label || 'End'} ({endNode.id.substring(0, 8)}...)
                                    </option>
                                  ))
                                }
                              </select>
                              <p className="text-xs text-muted-foreground">
                                When paired, the End node will loop back to this Start instead of terminating
                              </p>
                            </div>

                            {/* Show pairing status */}
                            {selectedNode.data.config?.pairedEndId && (
                              <div className="text-sm bg-green-50 p-3 rounded border border-green-200 flex items-center gap-2">
                                <span className="text-green-600">↻</span>
                                <span>
                                  Loop active: End node will return to "{selectedNode.data.config?.name || 'this Start'}"
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Goto Start Configuration */}
                    {selectedNode.data.type === 'goto_start' && (
                      <>
                        <div className="space-y-4">
                          <Label className="text-base font-semibold">Jump Target</Label>
                          <p className="text-xs text-muted-foreground">
                            Select which Start node execution should jump to when this node is reached
                          </p>

                          {/* Target Start Node Selector */}
                          <div className="space-y-2">
                            <Label htmlFor="target-start">Target Start Node</Label>
                            <select
                              id="target-start"
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              value={selectedNode.data.config?.targetStartId || ''}
                              onChange={(e) => {
                                const startNodeId = e.target.value;
                                const startNode = nodes.find(n => n.id === startNodeId);
                                const startName = startNode?.data.config?.name || startNode?.data.label || 'Unnamed Start';

                                // Update this Goto Start node's target
                                updateNodeConfig('targetStartId', startNodeId || undefined);
                                updateNodeConfig('targetStartName', startNodeId ? startName : undefined);
                              }}
                            >
                              <option value="">Select a Start node...</option>
                              {nodes
                                .filter(n => n.data?.type === 'start')
                                .map(startNode => (
                                  <option key={startNode.id} value={startNode.id}>
                                    {startNode.data.config?.name || startNode.data.label || 'Start'} ({startNode.id.substring(0, 8)}...)
                                  </option>
                                ))
                              }
                            </select>
                            <p className="text-xs text-muted-foreground">
                              Execution will jump to the selected Start node (one-way navigation)
                            </p>
                          </div>

                          {/* Show target status */}
                          {selectedNode.data.config?.targetStartId && (
                            <div className="text-sm bg-purple-50 p-3 rounded border border-purple-200 flex items-center gap-2">
                              <span className="text-purple-600">→</span>
                              <span>
                                Jumps to: {selectedNode.data.config?.targetStartName || 'Selected Start'}
                              </span>
                            </div>
                          )}

                          {!selectedNode.data.config?.targetStartId && (
                            <div className="text-sm bg-yellow-50 p-3 rounded border border-yellow-200 flex items-center gap-2">
                              <span className="text-yellow-600">⚠</span>
                              <span>No target selected - node will fail at runtime</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* SMS Configuration */}
                    {selectedNode.data.type === 'sms' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="sms-phone">Phone Number</Label>
                          <Input
                            id="sms-phone"
                            type="tel"
                            value={getTextInputValue(selectedNode.data.config?.parameters?.phoneNumber)}
                            onChange={(e) => updateNodeParameter('phoneNumber', e.target.value)}
                            placeholder="+1234567890"
                          />
                          <p className="text-xs text-muted-foreground">
                            Phone number in international format
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="sms-message">Message</Label>
                          <Textarea
                            id="sms-message"
                            value={getTextareaValue(selectedNode.data.config?.parameters?.messageContent)}
                            onChange={(e) => updateNodeParameter('messageContent', e.target.value)}
                            placeholder="Hello {{userName}}, your order is ready!"
                            rows={4}
                          />
                          <p className="text-xs text-muted-foreground">
                            Use variables: <code className="bg-gray-100 px-1 rounded">{'{{variableName}}'}</code> or <code className="bg-gray-100 px-1 rounded">{'${variableName}'}</code>
                          </p>
                          {allAvailableVariables.length > 0 && (
                            <div className="text-xs bg-blue-50 p-2 rounded border border-blue-200">
                              <strong>Available variables:</strong>{' '}
                              {allAvailableVariables.map((v, i) => (
                                <span key={`avail-${v.source}-${v.name}`}>
                                  <code className="bg-white px-1 rounded font-mono">{v.name}</code>
                                  {v.source === 'mqtt' && <span className="text-green-600 text-xs ml-1">[MQTT]</span>}
                                  {i < allAvailableVariables.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Email Configuration */}
                    {selectedNode.data.type === 'email' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="email-to">To (Email Address)</Label>
                          <Input
                            id="email-to"
                            type="email"
                            value={getTextInputValue(selectedNode.data.config?.parameters?.to)}
                            onChange={(e) => updateNodeParameter('to', e.target.value)}
                            placeholder="recipient@example.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email-subject">Subject</Label>
                          <Input
                            id="email-subject"
                            value={getTextInputValue(selectedNode.data.config?.parameters?.subject)}
                            onChange={(e) => updateNodeParameter('subject', e.target.value)}
                            placeholder="Order Confirmation for {{userName}}"
                          />
                          <p className="text-xs text-muted-foreground">
                            Supports variables: <code className="bg-gray-100 px-1 rounded">{'{{variableName}}'}</code>
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email-body">Message Body</Label>
                          <Textarea
                            id="email-body"
                            value={getTextareaValue(selectedNode.data.config?.parameters?.body)}
                            onChange={(e) => updateNodeParameter('body', e.target.value)}
                            placeholder="Dear {{userName}}, thank you for your order..."
                            rows={6}
                          />
                          <p className="text-xs text-muted-foreground">
                            Use variables: <code className="bg-gray-100 px-1 rounded">{'{{variableName}}'}</code> or <code className="bg-gray-100 px-1 rounded">{'${variableName}'}</code>
                          </p>
                          {allAvailableVariables.length > 0 && (
                            <div className="text-xs bg-blue-50 p-2 rounded border border-blue-200">
                              <strong>Available variables:</strong>{' '}
                              {allAvailableVariables.map((v, i) => (
                                <span key={`avail-${v.source}-${v.name}`}>
                                  <code className="bg-white px-1 rounded font-mono">{v.name}</code>
                                  {v.source === 'mqtt' && <span className="text-green-600 text-xs ml-1">[MQTT]</span>}
                                  {i < allAvailableVariables.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Webhook Configuration */}
                    {selectedNode.data.type === 'webhook' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="webhook-url">Webhook URL</Label>
                          <Input
                            id="webhook-url"
                            type="url"
                            value={getTextInputValue(selectedNode.data.config?.parameters?.url)}
                            onChange={(e) => updateNodeParameter('url', e.target.value)}
                            placeholder="https://api.example.com/webhook"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="webhook-method">HTTP Method</Label>
                          <select
                            id="webhook-method"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={getSelectValue(selectedNode.data.config?.parameters?.method, 'POST')}
                            onChange={(e) => updateNodeParameter('method', e.target.value)}
                          >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                            <option value="DELETE">DELETE</option>
                            <option value="PATCH">PATCH</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="webhook-body">Request Body (JSON)</Label>
                          <Textarea
                            id="webhook-body"
                            value={getTextareaValue(selectedNode.data.config?.parameters?.body)}
                            onChange={(e) => updateNodeParameter('body', e.target.value)}
                            placeholder='{"key": "value"}'
                            rows={4}
                            className="font-mono text-xs"
                          />
                        </div>
                      </>
                    )}

                    {/* Variable Assignment / Set Value Configuration */}
                    {(selectedNode.data.type === 'variable_assignment' || selectedNode.data.type === 'variable_set') && (
                      <>
                        {/* Target Type Selector */}
                        <div className="space-y-2">
                          <Label htmlFor="target-type">Target Type</Label>
                          <select
                            id="target-type"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={selectedNode.data.config?.targetType || 'variable'}
                            onChange={(e) => updateNodeConfig('targetType', e.target.value)}
                          >
                            <option value="variable">Workflow Variable</option>
                            <option value="element">Screen Element Property</option>
                          </select>
                        </div>

                        {/* Element Property Mode */}
                        {selectedNode.data.config?.targetType === 'element' && (
                          <>
                            <div className="space-y-2">
                              <Label htmlFor="element-id">Element ID</Label>
                              <select
                                id="element-id"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={selectedNode.data.config?.elementId || ''}
                                onChange={(e) => updateNodeConfig('elementId', e.target.value)}
                              >
                                <option value="">Select element...</option>
                                {screens.flatMap((screen) =>
                                  screen.definition.elements.map((element) => (
                                    <option key={`${screen.id}-${element.id}`} value={element.id}>
                                      {screen.name} → {element.id} ({element.type})
                                    </option>
                                  ))
                                )}
                              </select>
                              <p className="text-xs text-muted-foreground">
                                Select the screen element to modify
                              </p>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="property-name">Property</Label>
                              <select
                                id="property-name"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={selectedNode.data.config?.propertyName || ''}
                                onChange={(e) => updateNodeConfig('propertyName', e.target.value)}
                              >
                                <option value="">Select property...</option>
                                {(() => {
                                  const elementId = selectedNode.data.config?.elementId;
                                  if (!elementId) return null;

                                  // Find the element type
                                  let elementType = '';
                                  for (const screen of screens) {
                                    const el = screen.definition.elements.find((element) => element.id === elementId);
                                    if (el) {
                                      elementType = el.type;
                                      break;
                                    }
                                  }

                                  // Property lists by element type
                                  const ELEMENT_PROPERTIES: Record<string, string[]> = {
                                    text: ['text', 'color', 'fontSize', 'align'],
                                    button: ['label', 'bgColor', 'pressedColor', 'textColor', 'fontSize', 'enabled'],
                                    gauge: ['value', 'min', 'max', 'label', 'unit', 'bgColor', 'arcColor', 'textColor'],
                                    numeric_stepper: ['value', 'min', 'max', 'step', 'unit', 'bgColor', 'buttonColor', 'textColor'],
                                    rect: ['fillColor', 'borderColor', 'borderWidth', 'borderRadius'],
                                    header: ['text', 'bgColor', 'textColor', 'fontSize'],
                                  };

                                  const properties = ELEMENT_PROPERTIES[elementType] || [];
                                  return properties.map((prop) => (
                                    <option key={prop} value={prop}>{prop}</option>
                                  ));
                                })()}
                              </select>
                              <p className="text-xs text-muted-foreground">
                                Property to modify
                              </p>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="element-value">Value</Label>
                              <Input
                                id="element-value"
                                value={getJsonValueForTextInput(selectedNode.data.config?.value)}
                                onChange={(e) => updateNodeConfig('value', e.target.value)}
                                placeholder="Value or ${variable}"
                              />
                              <p className="text-xs text-muted-foreground">
                                Use ${'{'}variableName{'}'} to reference a variable
                              </p>
                            </div>
                          </>
                        )}

                        {/* Variable Mode (existing UI) */}
                        {(!selectedNode.data.config?.targetType || selectedNode.data.config?.targetType === 'variable') && (
                        <>
                        <div className="space-y-2">
                          <Label htmlFor="var-assign-name">Variable Name</Label>
                          <select
                            id="var-assign-name"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={selectedNode.data.config?.variableName || ''}
                            onChange={(e) => updateNodeConfig('variableName', e.target.value)}
                          >
                            <option value="">Select a variable...</option>
                            {allAvailableVariables.map((v) => (
                              <option key={`var-${v.source}-${v.name}`} value={v.name}>
                                {v.name} ({v.type}){v.source === 'mqtt' ? ' [MQTT]' : ''}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            Select a workflow variable or MQTT-mapped variable
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="var-assign-source">Value Source</Label>
                          <select
                            id="var-assign-source"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={selectedNode.data.config?.source || 'literal'}
                            onChange={(e) => updateNodeConfig('source', e.target.value)}
                          >
                            <option value="literal">Literal Value</option>
                            <option value="expression">Expression</option>
                            <option value="context">From Context</option>
                          </select>
                        </div>

                        {/* Literal Value */}
                        {(!selectedNode.data.config?.source || selectedNode.data.config?.source === 'literal') && (
                          <div className="space-y-2">
                            <Label htmlFor="var-assign-value">Value</Label>
                            {(() => {
                              const selectedVar = allAvailableVariables.find(
                                v => v.name === selectedNode.data.config?.variableName
                              );

                              if (selectedVar?.type === 'date') {
                                return (
                                  <div className="space-y-2">
                                    <Input
                                      id="var-assign-value"
                                      type="datetime-local"
                                      value={getDateTimeLocalValue(selectedNode.data.config?.value)}
                                      onChange={(e) => {
                                        updateNodeConfig('value', new Date(e.target.value).toISOString());
                                      }}
                                      className="font-mono text-sm"
                                    />
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="w-full gap-2"
                                      onClick={() => {
                                        updateNodeConfig('value', new Date().toISOString());
                                      }}
                                    >
                                      <Clock className="w-4 h-4" />
                                      Set to Current Time
                                    </Button>
                                    {selectedNode.data.config?.value && (
                                      <p className="text-xs text-muted-foreground">
                                        {getDateDisplayValue(selectedNode.data.config?.value)}
                                      </p>
                                    )}
                                  </div>
                                );
                              }

                              return (
                                <Input
                                  id="var-assign-value"
                                  value={getJsonValueForTextInput(selectedNode.data.config?.value)}
                                  onChange={(e) => {
                                    const value = selectedVar?.type === 'number'
                                      ? Number(e.target.value)
                                      : e.target.value;
                                    updateNodeConfig('value', value);
                                  }}
                                  type={selectedVar?.type === 'number' ? 'number' : 'text'}
                                  placeholder="Enter value"
                                />
                              );
                            })()}
                            <p className="text-xs text-muted-foreground">
                              Direct value to assign
                            </p>
                          </div>
                        )}

                        {/* Expression */}
                        {selectedNode.data.config?.source === 'expression' && (
                          <div className="space-y-2">
                            <Label htmlFor="var-assign-expression">Expression</Label>
                            <Input
                              ref={expressionInputRef}
                              id="var-assign-expression"
                              value={selectedNode.data.config?.expression || ''}
                              onChange={(e) => {
                                updateNodeConfig('expression', e.target.value);
                                setValidationResult(null); // Clear validation on change
                              }}
                              placeholder={(() => {
                                const selectedVar = workflowVariables.find(
                                  v => v.name === selectedNode.data.config?.variableName
                                );
                                if (selectedVar?.type === 'date') {
                                  return 'now() or addDays(now(), 7)';
                                }
                                return 'count + 1';
                              })()}
                              className="font-mono text-sm"
                            />
                            <div className="flex gap-2">
                              <div className="relative">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setShowVariablePicker(!showVariablePicker)}
                                  type="button"
                                >
                                  Variables
                                </Button>
                                {showVariablePicker && (
                                  <>
                                    <div
                                      className="fixed inset-0 z-40"
                                      onClick={() => setShowVariablePicker(false)}
                                    />
                                    <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-96 max-h-[32rem] overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-2xl">
                                      <div className="p-4 border-b bg-gray-50 sticky top-0">
                                        <h4 className="font-semibold text-base">Available Variables</h4>
                                        <p className="text-xs text-gray-600 mt-1">Click to insert into expression</p>
                                      </div>
                                      {(() => {
                                        const vars = getAvailableVariables();

                                        if (vars.length === 0) {
                                          return (
                                            <div className="p-4 text-center text-gray-500">
                                              No variables available. Define variables in the Variables tab or add MQTT mappings.
                                            </div>
                                          );
                                        }

                                        const grouped = vars.reduce((acc, v) => {
                                          if (!acc[v.source]) acc[v.source] = [];
                                          acc[v.source].push(v);
                                          return acc;
                                        }, {} as Record<string, typeof vars>);

                                        return (
                                          <div>
                                            {Object.entries(grouped).map(([source, variables]) => (
                                              <div key={source} className="border-b last:border-b-0">
                                                <div className="px-4 py-2 bg-gray-100 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                                  {source}
                                                </div>
                                                <div>
                                                  {variables.map((v, idx) => (
                                                    <button
                                                      key={`${v.name}-${idx}`}
                                                      type="button"
                                                      onClick={() => handleInsertVariable(v.name)}
                                                      className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors flex items-center justify-between group border-b last:border-b-0"
                                                    >
                                                      <span className="font-mono text-sm text-blue-700 group-hover:text-blue-800 font-medium">
                                                        {v.name}
                                                      </span>
                                                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                        {v.type}
                                                      </span>
                                                    </button>
                                                  ))}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </>
                                )}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleValidateExpression}
                                disabled={isValidating || !selectedNode.data.config?.expression}
                              >
                                {isValidating ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Validating...
                                  </>
                                ) : (
                                  'Validate'
                                )}
                              </Button>
                            </div>

                            {/* Validation Result */}
                            {validationResult && (
                              <div className={cn(
                                'p-3 rounded border text-sm',
                                validationResult.valid
                                  ? 'bg-green-50 border-green-200 text-green-800'
                                  : 'bg-red-50 border-red-200 text-red-800'
                              )}>
                                {validationResult.valid ? (
                                  <div>
                                    <div className="font-semibold mb-1">✓ Valid Expression</div>
                                    <div className="text-xs">
                                      Result: {(() => {
                                        const result = validationResult.result;
                                        if (result instanceof Date || (result && typeof result === 'object' && result.constructor?.name === 'Date')) {
                                          return getDateDisplayValue(result);
                                        }
                                        return JSON.stringify(result);
                                      })()}
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="font-semibold mb-1">✗ Invalid Expression</div>
                                    <div className="text-xs">{validationResult.error}</div>
                                  </div>
                                )}
                              </div>
                            )}

                            <p className="text-xs text-muted-foreground">
                              JavaScript expression (e.g., count + 1, price * quantity)
                            </p>

                            {/* Datetime Expression Helpers */}
                            {(() => {
                              const selectedVar = allAvailableVariables.find(
                                v => v.name === selectedNode.data.config?.variableName
                              );
                              return selectedVar?.type === 'date' ? (
                                <div className="space-y-2 pt-2">
                                  <Label className="text-xs font-semibold">Quick Date Expressions</Label>
                                  <div className="grid grid-cols-2 gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-8"
                                      onClick={() => updateNodeConfig('expression', 'now()')}
                                    >
                                      Current Time
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-8"
                                      onClick={() => updateNodeConfig('expression', 'addDays(now(), 1)')}
                                    >
                                      Tomorrow
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-8"
                                      onClick={() => updateNodeConfig('expression', 'addWeeks(now(), 1)')}
                                    >
                                      Next Week
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-8"
                                      onClick={() => updateNodeConfig('expression', 'addMonths(now(), 1)')}
                                    >
                                      Next Month
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-8"
                                      onClick={() => updateNodeConfig('expression', 'addHours(now(), 1)')}
                                    >
                                      +1 Hour
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-8"
                                      onClick={() => updateNodeConfig('expression', 'addDays(now(), -1)')}
                                    >
                                      Yesterday
                                    </Button>
                                  </div>
                                  <div className="text-xs bg-blue-50 p-3 rounded border border-blue-200 space-y-1">
                                    <div className="font-semibold mb-2">Date Functions:</div>
                                    <div><code className="bg-white px-1 rounded">now()</code> - Current date/time</div>
                                    <div><code className="bg-white px-1 rounded">addDays(date, n)</code> - Add/subtract days</div>
                                    <div><code className="bg-white px-1 rounded">addWeeks(date, n)</code> - Add/subtract weeks</div>
                                    <div><code className="bg-white px-1 rounded">addMonths(date, n)</code> - Add/subtract months</div>
                                    <div><code className="bg-white px-1 rounded">addYears(date, n)</code> - Add/subtract years</div>
                                    <div><code className="bg-white px-1 rounded">addHours(date, n)</code> - Add/subtract hours</div>
                                    <div><code className="bg-white px-1 rounded">addMinutes(date, n)</code> - Add/subtract minutes</div>
                                    <div className="pt-2 text-muted-foreground">
                                      Examples: <code className="bg-white px-1 rounded">addDays(now(), 7)</code>,
                                      <code className="bg-white px-1 rounded ml-1">addHours(startTime, -2)</code>
                                    </div>
                                  </div>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        )}

                        {/* Context Path */}
                        {selectedNode.data.config?.source === 'context' && (
                          <div className="space-y-2">
                            <Label htmlFor="var-assign-path">Context Path</Label>
                            <Input
                              id="var-assign-path"
                              value={selectedNode.data.config?.expression || ''}
                              onChange={(e) => updateNodeConfig('expression', e.target.value)}
                              placeholder="waterHeight"
                              className="font-mono text-sm"
                              title="Enter variable name or nested path (e.g., user.id)"
                            />
                            <p className="text-xs text-muted-foreground">
                              Access variables created from MQTT mappings or workflow variables
                            </p>

                            {/* Show available MQTT-mapped variables */}
                            {(() => {
                              const startNode = nodes.find((n) => n.data?.type === 'start');
                              const mqttMappings = (startNode?.data?.config?.variableMappings || [])
                                .filter((mapping) => mapping.targetVariable && mapping.targetVariable.trim() !== '');

                              if (mqttMappings.length > 0) {
                                return (
                                  <div className="text-xs bg-green-50 p-3 rounded border border-green-200">
                                    <strong className="text-green-900">Available from MQTT Trigger:</strong>
                                    <ul className="mt-1 space-y-1 ml-3">
                                      {mqttMappings.map((mapping, idx: number) => (
                                        <li key={idx} className="flex items-center gap-2">
                                          <code
                                            className="bg-green-100 px-1 py-0.5 rounded cursor-pointer hover:bg-green-200"
                                            onClick={() => updateNodeConfig('expression', mapping.targetVariable)}
                                            title="Click to use this variable"
                                          >
                                            {mapping.targetVariable}
                                          </code>
                                          <span className="text-green-700 text-xs">
                                            ← {mapping.sourcePath}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                    <div className="mt-2 pt-2 border-t border-green-200 text-green-700">
                                      💡 Click a variable name to use it
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div className="text-xs bg-gray-50 p-3 rounded border">
                                  <strong>Examples:</strong>
                                  <ul className="mt-1 space-y-1 ml-3">
                                    <li>• <code className="bg-white px-1 rounded">waterHeight</code> - Direct variable access</li>
                                    <li>• <code className="bg-white px-1 rounded">user.id</code> - Nested property access</li>
                                    <li>• <code className="bg-white px-1 rounded">data.items[0]</code> - Array element access</li>
                                  </ul>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        </>
                        )}
                      </>
                    )}

                    {/* Compose Schema Configuration */}
                    {selectedNode.data.type === 'compose_schema' && (
                      <div className="space-y-4">
                        {/* Initialize default outputVariable if not set */}
                        {!selectedNode.data.config?.outputVariable && (() => {
                          // Side-effect: save default immediately when section renders
                          setTimeout(() => updateNodeConfig('outputVariable', 'composedData'), 0);
                          return null;
                        })()}
                        {/* Schema Selector */}
                        <div className="space-y-2">
                          <Label htmlFor="compose-schema-select">
                            Schema <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={selectedNode.data.config?.schemaId || ''}
                            onValueChange={(value) => {
                              updateNodeConfig('schemaId', value);
                              // Clear field mappings when schema changes
                              updateNodeConfig('fieldMappings', {});
                              // Set default outputVariable if not already set
                              if (!selectedNode.data.config?.outputVariable) {
                                updateNodeConfig('outputVariable', 'composedData');
                              }
                            }}
                          >
                            <SelectTrigger id="compose-schema-select">
                              <SelectValue placeholder="Select a schema..." />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Include on-demand fetched schema if not already in list */}
                              {onDemandSchemaWithId && !schemas.some((schema) => schema.id === onDemandSchemaWithId.id) && (
                                <SelectItem key={onDemandSchemaWithId.id} value={onDemandSchemaWithId.id}>
                                  <span className="flex items-center gap-2">
                                    <Database className="h-3.5 w-3.5" />
                                    {onDemandSchemaWithId.name}
                                    {onDemandSchemaWithId.fields && (
                                      <Badge variant="outline" className="ml-1 text-[10px]">
                                        {onDemandSchemaWithId.fields.length} fields
                                      </Badge>
                                    )}
                                  </span>
                                </SelectItem>
                              )}
                              {schemasWithId.map((schema) => (
                                <SelectItem key={schema.id} value={schema.id}>
                                  <span className="flex items-center gap-2">
                                    <Database className="h-3.5 w-3.5" />
                                    {schema.name}
                                    {schema.fields && (
                                      <Badge variant="outline" className="ml-1 text-[10px]">
                                        {schema.fields.length} fields
                                      </Badge>
                                    )}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Select the schema that defines the structure of the composed object
                          </p>
                        </div>

                        {/* Output Variable Name */}
                        <div className="space-y-2">
                          <Label htmlFor="compose-output-var">
                            Output Variable Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="compose-output-var"
                            value={selectedNode.data.config?.outputVariable || 'composedData'}
                            onChange={(e) => updateNodeConfig('outputVariable', e.target.value)}
                            onFocus={() => {
                              // Ensure default value is saved to config on first focus
                              if (!selectedNode.data.config?.outputVariable) {
                                updateNodeConfig('outputVariable', 'composedData');
                              }
                            }}
                            placeholder="composedData"
                            className="font-mono"
                          />
                          <p className="text-xs text-muted-foreground">
                            Variable name to store the composed object (accessible by downstream nodes)
                          </p>
                        </div>

                        {/* Field Mappings */}
                        {selectedNode.data.config?.schemaId && (
                          <div className="space-y-2">
                            <Label>Field Mappings</Label>
                            <div className="border rounded-lg p-3 bg-gray-50">
                              <SchemaFieldMapper
                                schemaId={selectedNode.data.config.schemaId}
                                fieldMappings={selectedNode.data.config?.fieldMappings || {}}
                                onChange={(mappings) => updateNodeConfig('fieldMappings', mappings)}
                                availableVariables={allAvailableVariables.map((v) => ({
                                  name: v.name,
                                  type: v.type,
                                  source: v.source,
                                }))}
                              />
                            </div>
                          </div>
                        )}

                        {/* Validate On Compose */}
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="compose-validate">Validate on Compose</Label>
                            <p className="text-xs text-muted-foreground">
                              Validate the composed object against schema constraints
                            </p>
                          </div>
                          <Switch
                            id="compose-validate"
                            checked={selectedNode.data.config?.validateOnCompose !== false}
                            onCheckedChange={(checked) => updateNodeConfig('validateOnCompose', checked)}
                          />
                        </div>
                      </div>
                    )}

                    {/* Schema Data Store Configuration */}
                    {selectedNode.data.type === 'schema_data_store' && (
                      <SchemaDataStorePropertyPanel
                        node={selectedNode}
                        nodes={nodes}
                        edges={edges}
                        onChange={(config) => {
                          // Update node config with new values
                          setNodes(nds => nds.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, config: { ...n.data.config, ...config } } }
                              : n
                          ));
                        }}
                      />
                    )}

                    {/* Schema Data Query Configuration */}
                    {selectedNode.data.type === 'schema_data_query' && (
                      <SchemaDataQueryPropertyPanel
                        node={selectedNode}
                        nodes={nodes}
                        edges={edges}
                        onChange={(config) => {
                          // Update node config with new values
                          setNodes(nds => nds.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, config: { ...n.data.config, ...config } } }
                              : n
                          ));
                        }}
                      />
                    )}

                    {/* Binary Parser Configuration */}
                    {selectedNode.data.type === 'binary_parser' && (
                      <BinaryParserPropertyPanel
                        node={selectedNode}
                        nodes={nodes}
                        edges={edges}
                        onChange={(config) => {
                          // Update node config with new values
                          setNodes(nds => nds.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, config: { ...n.data.config, ...config } } }
                              : n
                          ));
                        }}
                      />
                    )}

                    {/* ADC Range Converter Configuration */}
                    {selectedNode.data.type === 'adc_range_converter' && (
                      <ADCRangeConverterPropertyPanel
                        node={selectedNode}
                        nodes={nodes}
                        edges={edges}
                        onChange={(config) => {
                          // Update node config with new values
                          setNodes(nds => nds.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, config: { ...n.data.config, ...config } } }
                              : n
                          ));
                        }}
                      />
                    )}

                    {/* Condition Configuration */}
                    {selectedNode.data.type === 'condition' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="condition-variable">Left Variable</Label>
                          <select
                            id="condition-variable"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={selectedNode.data.config?.field || ''}
                            onChange={(e) => updateNodeConfig('field', e.target.value)}
                          >
                            <option value="">Select a variable...</option>
                            {allAvailableVariables.map((v) => (
                              <option key={`var-${v.source}-${v.name}`} value={v.name}>
                                {v.name} ({v.type}){v.source === 'mqtt' ? ' [MQTT]' : ''}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            Select the variable to evaluate (workflow or MQTT-mapped)
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="condition-operator">Operator</Label>
                          <select
                            id="condition-operator"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={selectedNode.data.config?.operator || 'eq'}
                            onChange={(e) => updateNodeConfig('operator', e.target.value)}
                          >
                            <option value="eq">Equals (==)</option>
                            <option value="ne">Not Equals (!=)</option>
                            <option value="gt">Greater Than (&gt;)</option>
                            <option value="gte">Greater Than or Equal (&gt;=)</option>
                            <option value="lt">Less Than (&lt;)</option>
                            <option value="lte">Less Than or Equal (&lt;=)</option>
                            <option value="contains">Contains</option>
                            <option value="startsWith">Starts With</option>
                            <option value="endsWith">Ends With</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="condition-value">Right Value</Label>
                          <Input
                            id="condition-value"
                            value={getJsonValueForTextInput(selectedNode.data.config?.value)}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              // Get the selected variable to determine type
                              const selectedVar = allAvailableVariables.find(
                                v => v.name === selectedNode.data.config?.field
                              );

                              let typedValue: string | number | boolean = inputValue;

                              // Convert to appropriate type based on variable type
                              if (selectedVar?.type === 'number') {
                                // Don't convert while user is typing an incomplete decimal
                                // This allows typing "0." -> "0.1" without the "." being stripped
                                const isIncompleteNumber =
                                  inputValue === '' ||
                                  inputValue === '-' ||
                                  inputValue.endsWith('.') ||
                                  /\.\d*0$/.test(inputValue) ||  // trailing zeros after decimal
                                  /[eE][-+]?$/.test(inputValue); // incomplete exponent

                                if (!isIncompleteNumber) {
                                  const num = parseFloat(inputValue);
                                  typedValue = isNaN(num) ? inputValue : num;
                                }
                              } else if (selectedVar?.type === 'boolean') {
                                typedValue = inputValue === 'true' || inputValue === '1';
                              }
                              // For string, date, and object types, keep as string

                              updateNodeConfig('value', typedValue);
                            }}
                            placeholder="Enter comparison value"
                            className="font-mono text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            Value to compare against
                          </p>
                        </div>

                        {/* Expression Preview */}
                        {selectedNode.data.config?.field && selectedNode.data.config?.operator && (
                          <div className="space-y-2">
                            <Label>Condition Preview</Label>
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm font-mono">
                              {selectedNode.data.config?.field}{' '}
                              {selectedNode.data.config?.operator === 'eq' ? '==' :
                               selectedNode.data.config?.operator === 'ne' ? '!=' :
                               selectedNode.data.config?.operator === 'gt' ? '>' :
                               selectedNode.data.config?.operator === 'gte' ? '>=' :
                               selectedNode.data.config?.operator === 'lt' ? '<' :
                               selectedNode.data.config?.operator === 'lte' ? '<=' :
                               selectedNode.data.config?.operator}{' '}
                              {(() => {
                                const value = selectedNode.data.config?.value;
                                if (value === undefined || value === null || value === '') {
                                  return '(empty)';
                                }
                                // Numbers and booleans display without quotes
                                if (typeof value === 'number' || typeof value === 'boolean') {
                                  return String(value);
                                }
                                // Strings display with quotes
                                return JSON.stringify(value);
                              })()}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Display Screen node - special handling with screen dropdown */}
                    {selectedNode.data.type === 'display_screen' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Screen</Label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={selectedNode.data.config?.screenName || ''}
                            onChange={(e) => updateNodeConfig('screenName', e.target.value)}
                          >
                            <option value="">Select a screen...</option>
                            {screens.map((screen) => (
                              <option key={screen.id} value={screen.name}>
                                {screen.name}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            Select which screen to display on the ESP32
                          </p>
                        </div>
                        {screens.length === 0 && (
                          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                            No screens created yet. Go to the Screens tab to create one.
                          </div>
                        )}
                      </div>
                    )}

                    {/* Send Data node - sends data via Socket.IO to server */}
                    {selectedNode.data.type === 'send_data' && (
                      <div className="space-y-4">
                        {/* Data Type - required category identifier */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Data Type <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            type="text"
                            value={selectedNode.data.config?.dataType ?? ''}
                            onChange={(e) => updateNodeConfig('dataType', e.target.value)}
                            placeholder="sensor_reading"
                          />
                          <p className="text-xs text-muted-foreground">
                            Category of data (e.g., sensor_reading, status, alert). Used by server workflows to filter incoming data.
                          </p>
                        </div>

                        {/* Schema Selector - optional for server-side validation */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Schema (Optional)
                          </Label>
                          <Select
                            value={selectedNode.data.config?.schemaId || 'none'}
                            onValueChange={(value) => {
                              const newSchemaId = value === 'none' ? '' : value;
                              updateNodeConfig('schemaId', newSchemaId);
                              // Clear fieldMappings and variables when schema changes
                              updateNodeConfig('fieldMappings', {});
                              updateNodeConfig('variables', []);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a schema for validation..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {schemasWithId.map((schema) => (
                                <SelectItem key={schema.id} value={schema.id}>
                                  <span className="flex items-center gap-2">
                                    <Database className="h-3.5 w-3.5" />
                                    {schema.name}
                                    {schema.fields && (
                                      <Badge variant="outline" className="ml-1 text-[10px]">
                                        {schema.fields.length} fields
                                      </Badge>
                                    )}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            {selectedNode.data.config?.schemaId
                              ? 'Map schema fields to workflow variables below.'
                              : 'Schema for server-side validation. Leave empty to send unvalidated data.'}
                          </p>
                        </div>

                        {/* Custom ID - optional identifier for server workflow filtering */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Custom ID (Optional)
                          </Label>
                          <Input
                            type="text"
                            value={selectedNode.data.config?.customId ?? ''}
                            onChange={(e) => updateNodeConfig('customId', e.target.value)}
                            placeholder="greenhouse-sensor-1"
                          />
                          <p className="text-xs text-muted-foreground">
                            User-defined identifier for filtering in server workflows. Useful for routing data from multiple nodes.
                          </p>
                        </div>

                        {/* Field Mappings (when schema selected) OR Variables to Include (no schema) */}
                        {selectedNode.data.config?.schemaId ? (
                          /* Schema selected: Show schema fields with variable mapping */
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">
                              Field Mappings <span className="text-red-500">*</span>
                            </Label>
                            <div className="border rounded-lg p-3 bg-gray-50">
                              <SchemaFieldMapper
                                schemaId={selectedNode.data.config.schemaId}
                                fieldMappings={selectedNode.data.config?.fieldMappings || {}}
                                onChange={(mappings) => updateNodeConfig('fieldMappings', mappings)}
                                availableVariables={allAvailableVariables.map((v) => ({
                                  name: v.name,
                                  type: v.type,
                                  source: v.source,
                                }))}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Map each schema field to a workflow variable. The data will be sent using schema field names as keys.
                            </p>
                          </div>
                        ) : (
                          /* No schema: Show variable checkboxes */
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">
                              Variables to Include <span className="text-red-500">*</span>
                            </Label>
                            <div className="border rounded-md p-3 max-h-[200px] overflow-y-auto space-y-2">
                              {allAvailableVariables.length === 0 ? (
                                <p className="text-xs text-muted-foreground">
                                  No variables available. Add variables in the Variables tab or configure MQTT mappings in the Start node.
                                </p>
                              ) : (
                                allAvailableVariables.map((variable) => {
                                  const selectedVars = selectedNode.data.config?.variables || [];
                                  const isSelected = selectedVars.includes(variable.name);
                                  return (
                                    <label
                                      key={variable.name}
                                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent/50 p-1 rounded"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          const newVars = e.target.checked
                                            ? [...selectedVars, variable.name]
                                            : selectedVars.filter((v: string) => v !== variable.name);
                                          updateNodeConfig('variables', newVars);
                                        }}
                                        className="rounded border-gray-300"
                                      />
                                      <span>{variable.name}</span>
                                      <Badge variant="outline" className="text-[10px]">
                                        {variable.type}
                                      </Badge>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Select which workflow variables to include in the payload sent to the server.
                            </p>
                          </div>
                        )}

                        {/* Options */}
                        <div className="space-y-3 border-t pt-3">
                          <Label className="text-sm font-medium">Options</Label>

                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedNode.data.config?.includeTimestamp ?? true}
                              onChange={(e) => updateNodeConfig('includeTimestamp', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            <span>Include Timestamp</span>
                          </label>

                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedNode.data.config?.includeDeviceInfo ?? true}
                              onChange={(e) => updateNodeConfig('includeDeviceInfo', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            <span>Include Device Info</span>
                            <span className="text-xs text-muted-foreground">(deviceId, rssi, firmware)</span>
                          </label>

                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedNode.data.config?.enableBuffer ?? false}
                              onChange={(e) => updateNodeConfig('enableBuffer', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            <span>Enable Offline Buffering</span>
                          </label>
                        </div>

                        {/* Min Interval */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Rate Limit (ms)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={selectedNode.data.config?.minInterval ?? 0}
                            onChange={(e) => updateNodeConfig('minInterval', parseInt(e.target.value) || 0)}
                            placeholder="0"
                          />
                          <p className="text-xs text-muted-foreground">
                            Minimum milliseconds between sends. 0 = no rate limiting.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Button Check node - special handling with button dropdown from screens */}
                    {selectedNode.data.type === 'button_check' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Button Element <span className="text-red-500">*</span>
                          </Label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={selectedNode.data.config?.buttonId || ''}
                            onChange={(e) => updateNodeConfig('buttonId', e.target.value)}
                          >
                            <option value="">Select a button...</option>
                            {availableButtonElements.map(({ screen, button }) => (
                              <option key={`${screen.id}-${button.id}`} value={button.id}>
                                {screen.name} → {getScreenElementLabel(button)}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            Select which button to check. The workflow will follow the &quot;pressed&quot; path if clicked, or &quot;not_pressed&quot; otherwise.
                          </p>
                        </div>
                        {availableButtonElements.length === 0 && (
                          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                            No buttons found on any screen. Add buttons to your screens first.
                          </div>
                        )}
                      </div>
                    )}

                    {/* UI Data Output node - terminal node for Data Source workflows */}
                    {selectedNode.data.type === 'ui_data_output' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Data Variable</Label>
                          <Input
                            type="text"
                            value={selectedNode.data.config?.dataVariable ?? 'data'}
                            onChange={(e) => updateNodeConfig('dataVariable', e.target.value)}
                            placeholder="data"
                          />
                          <p className="text-xs text-muted-foreground">
                            Name of the workflow variable containing the data array to output. The variable should hold an array of objects that will be passed to the UI component (chart, table, gauge).
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Dynamic property rendering for other registry-based nodes */}
                    {!['start', 'sms', 'email', 'webhook', 'variable_assignment', 'variable_set', 'condition', 'display_screen', 'button_check', 'send_data', 'ui_data_output', 'compose_schema', 'schema_data_store', 'binary_parser', 'adc_range_converter'].includes(selectedNode.data.type) && (
                      (() => {
                        const nodeDef = getNodeDefinition(selectedNode.data.type);
                        if (nodeDef?.properties && nodeDef.properties.length > 0) {
                          return (
                            <div className="space-y-4">
                              {nodeDef.properties.map((prop) => (
                                <div key={prop.name} className="space-y-2">
                                  <Label className="text-sm font-medium">
                                    {prop.label || prop.name}
                                    {prop.required && <span className="text-red-500 ml-1">*</span>}
                                  </Label>
                                  {prop.type === 'select' && prop.dynamicOptions ? (
                                    <DynamicDeviceSelect
                                      value={String(getSelectValue(selectedNode.data.config?.[prop.name], ''))}
                                      onChange={(val) => updateNodeConfig(prop.name, val)}
                                      required={prop.required}
                                    />
                                  ) : prop.type === 'select' && prop.options ? (
                                    <select
                                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                      value={getSelectValue(selectedNode.data.config?.[prop.name], String(prop.default ?? ''))}
                                      onChange={(e) => updateNodeConfig(prop.name, e.target.value)}
                                    >
                                      {!prop.required && <option value="">Select...</option>}
                                      {prop.options.map((opt) => (
                                        <option key={String(opt.value)} value={String(opt.value)}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  ) : prop.type === 'boolean' ? (
                                    <select
                                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                      value={String(selectedNode.data.config?.[prop.name] ?? prop.default ?? false)}
                                      onChange={(e) => updateNodeConfig(prop.name, e.target.value === 'true')}
                                    >
                                      <option value="false">False</option>
                                      <option value="true">True</option>
                                    </select>
                                  ) : prop.type === 'number' ? (
                                    <Input
                                      type="number"
                                      value={getTextInputValue(selectedNode.data.config?.[prop.name], String(prop.default ?? ''))}
                                      onChange={(e) => updateNodeConfig(prop.name, parseFloat(e.target.value) || 0)}
                                      min={prop.min}
                                      max={prop.max}
                                      placeholder={prop.placeholder}
                                    />
                                  ) : prop.type === 'color' ? (
                                    <div className="flex gap-2">
                                      <input
                                        type="color"
                                        value={String(getTextInputValue(selectedNode.data.config?.[prop.name], String(prop.default || '#000000')))}
                                        onChange={(e) => updateNodeConfig(prop.name, e.target.value)}
                                        className="h-10 w-14 rounded border cursor-pointer"
                                      />
                                      <Input
                                        type="text"
                                        value={getTextInputValue(selectedNode.data.config?.[prop.name], String(prop.default ?? ''))}
                                        onChange={(e) => updateNodeConfig(prop.name, e.target.value)}
                                        placeholder="#000000"
                                        className="flex-1"
                                      />
                                    </div>
                                  ) : (
                                    <Input
                                      type="text"
                                      value={getTextInputValue(selectedNode.data.config?.[prop.name], String(prop.default ?? ''))}
                                      onChange={(e) => updateNodeConfig(prop.name, e.target.value)}
                                      placeholder={prop.placeholder}
                                    />
                                  )}
                                  {prop.description && (
                                    <p className="text-xs text-muted-foreground">{prop.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return (
                          <div className="text-sm text-muted-foreground">
                            No specific configuration available for this node type
                          </div>
                        );
                      })()
                    )}
                  </TabsContent>
                  <TabsContent value="data" className="space-y-4 mt-4">
                    <div className="text-xs font-mono bg-gray-100 p-2 rounded max-h-40 overflow-auto">
                      {JSON.stringify(selectedNode.data, null, 2)}
                    </div>
                  </TabsContent>
                </Tabs>

                <Separator />

                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full gap-2"
                  onClick={deleteSelectedNodes}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Node
                </Button>
                  </>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center text-muted-foreground">
                <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No node selected</p>
                <p className="text-xs mt-1">Click on a node to view and edit its properties</p>
              </div>
            </div>
          )}
    </>
  );
};
